/**
 * Central, typed React Query key factory.
 *
 * Keep query keys here (not inline in components) so they stay consistent
 * with the invalidation map in `contexts/RealtimeContext`. Add one entry per
 * domain as features are migrated onto the data-access layer.
 */
export const queryKeys = {
  prijstest: {
    all: ['prijstest'] as const,
    woningen: ['prijstest', 'woningen'] as const,
    woning: (opnameId: string) => ['prijstest', 'woning', opnameId] as const,
    typen: ['prijstest', 'typen'] as const,
    antwoorden: ['prijstest', 'antwoorden'] as const,
    markeringen: ['prijstest', 'markeringen'] as const,
  },
  aiTraining: {
    all: ['ai-training'] as const,
    fotos: ['ai-training', 'fotos'] as const,
    overzicht: ['ai-training', 'overzicht'] as const,
    labels: (fotoId: string) => ['ai-training', 'labels', fotoId] as const,
    ai: (fotoId: string) => ['ai-training', 'ai', fotoId] as const,
  },
  aiMeetstaat: {
    all: ['ai-meetstaat'] as const,
    opnames: ['ai-meetstaat', 'opnames'] as const,
    opname: (opnameId: string) => ['ai-meetstaat', 'opname', opnameId] as const,
  },
  calculatie: {
    bewerkingen: ['calculatie', 'bewerkingen'] as const,
    onderdeelPrijzen: (onderdeelId: string) =>
      ['calculatie', 'onderdeel-prijzen', onderdeelId] as const,
  },
  naloop: {
    all: ['naloop'] as const,
    lijst: (filters: { status?: string; bron?: string; toegewezenAan?: string }) =>
      ['naloop', 'lijst', filters] as const,
    voorTaak: (taakId: string) => ['naloop', 'taak', taakId] as const,
    refs: (bron: string) => ['naloop', 'refs', bron] as const,
    bijlagen: (sleutel: string) => ['naloop', 'bijlagen', sleutel] as const,
    openSignalen: ['naloop', 'open-signalen'] as const,
    stelposten: ['naloop', 'stelposten-te-bevestigen'] as const,
  },
  financeFlags: {
    all: ['finance-flags'] as const,
    list: (afgehandeld: boolean) => ['finance-flags', 'list', afgehandeld] as const,
  },
  handboek: {
    all: ['handboek'] as const,
    pagina: (slug: string) => ['handboek', slug] as const,
  },
  koppelingen: {
    all: ['koppelingen'] as const,
    leadWebhookInbox: ['koppelingen', 'lead_webhook_inbox'] as const,
  },
  leads: {
    all: ['leads'] as const,
    list: (role?: string, userId?: string) => ['leads', 'list', role, userId] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
    activities: (leadId: string) => ['leads', leadId, 'activities'] as const,
  },
  projects: {
    all: ['projects'] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    /** Tabblad Projecten op het dashboard; realtime patcht deze per rij. */
    dashboardLijst: ['projects', 'dashboard-lijst'] as const,
  },
  offertes: {
    all: ['offertes'] as const,
    kleurbevestiging: (quoteId: string) => ['offertes', 'kleurbevestiging', quoteId] as const,
    kleurRegels: (calculatieId: string) => ['offertes', 'kleurregels', calculatieId] as const,
    project: (projectId: string) => ['offertes', 'project', projectId] as const,
    kleurbevestigingVoorbeeld: (quoteId: string, afzender: string) =>
      ['offertes', 'kleurbevestiging-voorbeeld', quoteId, afzender] as const,
  },
  meerwerk: {
    all: ['meerwerk'] as const,
    pipeline: ['meerwerk', 'pipeline'] as const,
    offertes: (projectId: string) => ['meerwerk', 'offertes', projectId] as const,
    spelregels: (projectId: string) => ['meerwerk', 'spelregels', projectId] as const,
    momentopnames: (projectId: string) => ['meerwerk', 'momentopnames', projectId] as const,
    klantvraag: (offerteId: string) => ['meerwerk', 'klantvraag', offerteId] as const,
  },
  softwareLijst: {
    rol: (userId?: string) => ['software-lijst', 'rol', userId] as const,
    meekijkers: ['software-lijst', 'meekijkers'] as const,
    kandidaten: ['software-lijst', 'kandidaten'] as const,
  },
  meldingen: {
    alles: ['meldingen'] as const,
    feedbackGevraagd: (userId?: string) => ['meldingen', 'feedback-gevraagd', userId] as const,
    feedbackFotos: (paden: readonly string[]) =>
      ['meldingen', 'feedback-fotos', paden.join('|')] as const,
  },
  overleg: {
    berichten: (anker: string) => ['overleg', 'berichten', anker] as const,
    collegas: ['overleg', 'collegas'] as const,
  },
  contacts: {
    all: ['contacts'] as const,
    detail: (id: string) => ['contacts', 'detail', id] as const,
  },
  email: {
    bijlagen: (emailId: string) => ['email', 'bijlagen', emailId] as const,
    herstelrapport: (projectId: string) => ['email', 'herstelrapport', projectId] as const,
  },
  werksoorten: {
    all: ['werksoorten'] as const,
    lijst: ['werksoorten', 'lijst'] as const,
    aliassen: ['werksoorten', 'aliassen'] as const,
  },
  uitbestedingen: {
    all: ['uitbestedingen'] as const,
    overview: ['uitbestedingen', 'overview'] as const,
    perOnderaannemer: ['uitbestedingen', 'per_onderaannemer'] as const,
    pool: ['uitbestedingen', 'pool'] as const,
    werkvoorraad: ['uitbestedingen', 'werkvoorraad'] as const,
    instellingen: ['uitbestedingen', 'instellingen'] as const,
    wkaStatus: ['uitbestedingen', 'wka_status'] as const,
    pijplijn: ['uitbestedingen', 'pijplijn'] as const,
    clustercontracten: ['uitbestedingen', 'clustercontracten'] as const,
    beschikbaarheid: ['uitbestedingen', 'beschikbaarheid'] as const,
    medewerkerAanvragen: ['uitbestedingen', 'medewerker_aanvragen'] as const,
    termijnstaffel: ['uitbestedingen', 'termijnstaffel'] as const,
    termijnstaat: (contractId: string) => ['uitbestedingen', 'termijnstaat', contractId] as const,
    termijnbonFotos: (installmentId: string) =>
      ['uitbestedingen', 'termijnbon_fotos', installmentId] as const,
  },
  oaDelen: {
    all: ['oa-delen'] as const,
    item: (soort: string, itemId: string) => ['oa-delen', soort, itemId] as const,
    contracten: (projectId: string) => ['oa-delen', 'contracten', projectId] as const,
  },
  oaPortal: {
    all: ['oa-portal'] as const,
    ploegen: ['oa-portal', 'ploegen'] as const,
    aanbiedingen: ['oa-portal', 'aanbiedingen'] as const,
    medewerkers: ['oa-portal', 'medewerkers'] as const,
    gereedMeldingen: (projectId: string) => ['oa-portal', 'gereed-meldingen', projectId] as const,
    houtrotPunten: (projectId: string) => ['oa-portal', 'houtrot-punten', projectId] as const,
    projecten: ['oa-portal', 'projecten'] as const,
    wkaDossier: ['oa-portal', 'wka-dossier'] as const,
    wkaTermijnen: ['oa-portal', 'wka-termijnen'] as const,
    urenAll: ['oa-portal', 'uren'] as const,
    uren: (van: string, tot: string) => ['oa-portal', 'uren', van, tot] as const,
    urenVerzoeken: ['oa-portal', 'uren-verzoeken'] as const,
    mandagenregister: (van: string, tot: string, projectId: string, contactId: string) =>
      ['oa-portal', 'mandagenregister', van, tot, projectId, contactId] as const,
  },
  purchaseOrders: {
    all: ['purchase_orders'] as const,
    projectOptions: ['purchase_orders', 'project_options'] as const,
  },
  finance: {
    dashboard: ['finance', 'dashboard'] as const,
    actiepunten: ['finance', 'actiepunten'] as const,
    cockpitTellers: ['finance', 'cockpit_tellers'] as const,
    openBatches: ['finance', 'open_batches'] as const,
    administratieHome: (userId: string) => ['finance', 'administratie_home', userId] as const,
    cashflowBasis: ['finance', 'cashflow_basis'] as const,
    bonnenTellers: ['finance', 'bonnen_tellers'] as const,
    reminderSettings: ['finance', 'reminder_settings'] as const,
    ledgerCategories: ['finance', 'ledger_categories'] as const,
    ledgerRules: ['finance', 'ledger_rules'] as const,
    supplierPoSettings: ['finance', 'supplier_po_settings'] as const,
    approvalThreshold: ['finance', 'approval_threshold'] as const,
    approvalSla: ['finance', 'approval_sla'] as const,
    btwOverzicht: ['finance', 'btw_overzicht'] as const,
    wv: (kind: string, year: number, index: number) =>
      ['finance', 'wv', kind, year, index] as const,
    vat: (year: number, quarter: number) => ['finance', 'vat', year, quarter] as const,
    balance: ['finance', 'balance'] as const,
    rubriceringWorklist: ['finance', 'rubricering_worklist'] as const,
  },
  aiOrg: {
    agents: ['ai_org', 'agents'] as const,
    agent: (code: string) => ['ai_org', 'agent', code] as const,
    instructieVersies: (code: string) => ['ai_org', 'instructie_versies', code] as const,
    runStats: ['ai_org', 'run_stats'] as const,
    prestaties: (code: string) => ['ai_org', 'prestaties', code] as const,
    proposals: (filter: string) => ['ai_org', 'proposals', filter] as const,
    openProposalsCount: ['ai_org', 'proposals', 'open_count'] as const,
    voiceSetup: ['ai_org', 'voice_setup'] as const,
    mission: {
      kpis: ['ai_org', 'mission', 'kpis'] as const,
      agents: ['ai_org', 'mission', 'agents'] as const,
      feed: ['ai_org', 'mission', 'feed'] as const,
      projects: ['ai_org', 'mission', 'projects'] as const,
      weer: ['ai_org', 'mission', 'weer'] as const,
    },
  },
  verf: {
    bestellingenOverzicht: (
      vestiging: string,
      periode: string,
      zoek: string,
      pagina: number,
      perPagina: number,
    ) => ['verf', 'bestellingen-overzicht', vestiging, periode, zoek, pagina, perPagina] as const,
    projectBestellingen: (projectId: string) =>
      ['verf', 'project-bestellingen', projectId] as const,
  },
  inkoop: {
    purchaseOrders: ['inkoop', 'purchase_orders'] as const,
    /** Basis-key om alle afwijkingen-lijsten in één keer te verversen. */
    termijnbonAfwijkingenAlle: ['inkoop', 'termijnbon_afwijkingen'] as const,
    termijnbonAfwijkingen: (contractIds: string[]) =>
      ['inkoop', 'termijnbon_afwijkingen', [...contractIds].sort().join(',')] as const,
    termijnbonExtraGrens: ['inkoop', 'termijnbon_extra_grens'] as const,
    bankPaymentTotals: (invoiceIds: string[]) =>
      ['inkoop', 'bank_payment_totals', [...invoiceIds].sort().join(',')] as const,
    /** Basis-key om alle betaalhistories in één keer te verversen. */
    factuurBetalingenAlle: ['inkoop', 'factuur_betalingen'] as const,
    factuurBetalingen: (invoiceId: string) => ['inkoop', 'factuur_betalingen', invoiceId] as const,
    zelfdeBedrag: (
      inboxId: string,
      supplier: string,
      invoiceNumber: string,
      amount: number,
      invoiceDate: string,
    ) =>
      ['inkoop', 'zelfde_bedrag', inboxId, supplier, invoiceNumber, amount, invoiceDate] as const,
  },
  banking: {
    mollieSettlement: (txId: string) => ['banking', 'mollie_settlement', txId] as const,
    mollieSettledIds: (txIds: string[]) =>
      ['banking', 'mollie_settled_ids', [...txIds].sort().join(',')] as const,
    mollieSpecs: (txIds: string[]) =>
      ['banking', 'mollie_specs', [...txIds].sort().join(',')] as const,
    mollieSpecForInvoice: (invoiceId: string) =>
      ['banking', 'mollie_spec_invoice', invoiceId] as const,
    mollieSpecsOntbrekend: ['banking', 'mollie_specs_ontbrekend'] as const,
    mollieMonitor: ['banking', 'mollie_monitor'] as const,
    suspectMatches: ['banking', 'suspect_matches'] as const,
    voorstellen: ['banking', 'voorstellen'] as const,
    controleAchteraf: ['banking', 'controle_achteraf'] as const,
    kandidaatFacturen: ['banking', 'kandidaat_facturen'] as const,
    transactieKoppelingen: ['banking', 'transactie_koppelingen'] as const,
    geleerdeAdviezen: ['banking', 'geleerde_adviezen'] as const,
    autoMatchLog: ['banking', 'automatch_log'] as const,
    paymentRemainders: ['banking', 'payment_remainders'] as const,
    financeCheck: ['banking', 'finance_check'] as const,
    runRapporten: (limit: number) => ['banking', 'run_rapporten', limit] as const,
    unbookedGroups: ['banking', 'unbooked_groups'] as const,
    bewakingTellers: (ouderDanDagen: number) =>
      ['banking', 'bewaking_tellers', ouderDanDagen] as const,
    recentLedgerBookings: ['banking', 'recent_ledger_bookings'] as const,
    overpaidInvoices: ['banking', 'overpaid_invoices'] as const,
    actiepunten: ['banking', 'actiepunten'] as const,
    factuurTransacties: (kant: 'verkoop' | 'inkoop', invoiceId: string) =>
      ['banking', 'factuur_transacties', kant, invoiceId] as const,
    transactieFactuurLinks: (txIds: string[]) =>
      ['banking', 'transactie_factuur_links', [...txIds].sort().join(',')] as const,
    /** Basis-key: invalidatie na koppel-/beoordeelacties raakt alle projecten. */
    projectFactuurHistorie: ['banking', 'project_factuur_historie'] as const,
    projectFactuurHistorieVoor: (salesInvoiceId: string) =>
      ['banking', 'project_factuur_historie', salesInvoiceId] as const,
    /** Basis-key: invalidatie na koppel-/verklaaracties raakt alle transacties. */
    genoemdeVoldaneFacturen: ['banking', 'genoemde_voldane_facturen'] as const,
    genoemdeVoldaneFacturenVoor: (txId: string) =>
      ['banking', 'genoemde_voldane_facturen', txId] as const,
    /** Basis-key: invalidatie na koppel-/verklaaracties raakt alle transacties. */
    genoemdeDocumenten: ['banking', 'genoemde_documenten'] as const,
    genoemdeDocumentenVoor: (txId: string) => ['banking', 'genoemde_documenten', txId] as const,
    aiKandidatenVoor: (txId: string) => ['banking', 'ai_kandidaten', txId] as const,
    batchHerkomstVoor: (txId: string) => ['banking', 'batch_herkomst', txId] as const,
  },
  communicatie: {
    all: ['communicatie'] as const,
    drempels: ['communicatie', 'drempels'] as const,
    overzicht: (bereik: string, userId: string | null) =>
      ['communicatie', 'overzicht', bereik, userId] as const,
    projectOnderaannemers: (projectId: string) =>
      ['communicatie', 'project-onderaannemers', projectId] as const,
  },
  phoneCalls: {
    all: ['phone_calls'] as const,
    register: ['phone_calls', 'register'] as const,
    forLead: (id: string) => ['phone_calls', 'lead', id] as const,
    forContact: (id: string) => ['phone_calls', 'contact', id] as const,
    forProject: (id: string) => ['phone_calls', 'project', id] as const,
  },
  mijnMeldingen: {
    all: ['mijn-meldingen'] as const,
    voorUser: (userId: string) => ['mijn-meldingen', userId] as const,
  },
} as const;
